import { useEffect, useMemo, useState } from "react";
import { Loader2, UserCheck, AlertTriangle } from "lucide-react";
import { getMySourceConnections } from "../../../../../api/marketplace";
import { ordersAssignCollaboration } from "../../../../../api/auth";
import { usePublicEntities } from "../../../../../hooks/usePublicEntities";

/**
 * Affectation MANUELLE d'une commande à un agent / une agence (collaborations
 * de type "Manuel"). Le backend refuse une double affectation :
 *  - ORDER_COVERED_BY_COLLABORATION : la boutique ou le produit est déjà
 *    affecté automatiquement à un autre prestataire ;
 *  - ORDER_ALREADY_ASSIGNED : déjà affectée à la main -> "Déplacer" (reassign).
 */
export function ManualAssignmentPanel({ order }) {
  const [state, setState] = useState({ loading: true, rules: [], token: null, error: "" });
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // {type, text, canReassign}

  useEffect(() => {
    let alive = true;
    getMySourceConnections()
      .then((res) => {
        if (!alive) return;
        const rules = (res.data?.assignment_rules || []).filter(
          (r) =>
            r.assignment_type === "MANUAL" &&
            (!r.source_connection_id || String(r.source_connection_id) === String(order.source_connection || ""))
        );
        setState({ loading: false, rules, token: res.data?.assignment_token, error: "" });
      })
      .catch(() => alive && setState({ loading: false, rules: [], token: null, error: "Collaborations indisponibles." }));
    return () => {
      alive = false;
    };
  }, [order.source_connection]);

  const providers = useMemo(
    () => state.rules.map((r) => ({ type: r.provider_type, id: r.provider_id })),
    [state.rules]
  );
  const { getEntity } = usePublicEntities(providers);

  const assign = async (reassign = false) => {
    if (!selected) return;
    setSaving(true);
    setResult(null);
    try {
      await ordersAssignCollaboration(order.id, {
        collaboration_id: selected,
        assignment_token: state.token,
        ...(reassign ? { reassign: true } : {}),
      });
      setResult({ type: "success", text: "Commande affectée." });
    } catch (err) {
      const data = err?.response?.data || {};
      setResult({
        type: "error",
        text: data.detail || "Affectation impossible.",
        canReassign: data.code === "ORDER_ALREADY_ASSIGNED",
      });
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement des collaborations…
      </div>
    );
  }
  if (state.error) return <p className="text-sm text-rose-600">{state.error}</p>;
  if (!state.rules.length) {
    return (
      <p className="empty-state rounded-lg bg-slate-50 p-3 text-sm text-slate-500">
        Aucune collaboration en mode « Manuel » active. Les commandes des boutiques ou produits affectés
        automatiquement sont déjà gérées par leur prestataire.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value);
            setResult(null);
          }}
          className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">Choisir un agent / une agence…</option>
          {state.rules.map((r) => (
            <option key={r.collaboration_id} value={r.collaboration_id}>
              {getEntity(r.provider_type, r.provider_id)?.display_name ||
                (r.provider_type === "AGENCY_OWNER" ? "Agence" : "Agent")}
            </option>
          ))}
        </select>
        <button type="button" onClick={() => assign(false)} disabled={!selected || saving} className="btn-primary disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
          Affecter
        </button>
      </div>

      {result && (
        <div
          className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${
            result.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
          }`}
        >
          {result.type === "error" && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span className="flex-1">{result.text}</span>
          {result.canReassign && (
            <button type="button" onClick={() => assign(true)} disabled={saving} className="shrink-0 font-semibold underline">
              Déplacer vers ce prestataire
            </button>
          )}
        </div>
      )}
    </div>
  );
}
